import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogContent,
  FormHelperText,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { t } from "i18next";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import AppLoader from "../../../components/AppLoader";
import GlobalButton from "../../../components/CustomButton";
import Label from "../../../components/Label";
import TextInput from "../../../components/TextInputs";
import { CURRENCY } from "../../../constants/Variables";
import globalStyle from "../../../globalStyle";
import { baseUrl } from "../../../services";
import { updateCompanyOwnSerivce } from "../../../services/company/serviceCategoryApiFn";
import { handleOpenSnackBar } from "../../../utils";
import { useSelector } from "react-redux";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2.5),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing("10px 16px"),
  },
  "& .MuiPaper-root": {
    width: "500px",
  },
}));

let paramsData = {};

const EditServiceDialog = forwardRef(({}, ref) => {
  const openImageFile = useRef(null);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [prices, setPrices] = useState({});
  const [inputValues, setInputValues] = useState({
    image: null,
    name: "",
    description: "",
    percentage: "",
    companyPercentage: "",
    activeIcon: null,
  });
  const { currentLanguage } = useSelector((state) => state.translationReducer);

  const [inputError, setInputError] = useState({
    area: false,
    room: false,
    fixed: false,
    interior: false,
    exterior: false,
    companyPercentage: false,
    isValidPercentage: false,
  });

  useImperativeHandle(ref, () => ({
    isVisible(params) {
      setOpen(true);
      paramsData = params;
      if (params.type === "edit") {
        setInputValues({
          image: params.data.serviceSubCategoryId.image,
          name: params.data.serviceSubCategoryId.name,
          description: params.data.serviceSubCategoryId.description,
          percentage: params.data.serviceSubCategoryId.percentage,
          companyPercentage: params.data.percentage || 0,
          activeIcon: params.data.serviceSubCategoryId.activeIcon,
        });
        setPrices(params?.data?.price);
      }
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputValues({});
    setInputError({});
    setPrices({});
  };

  const handleUploadImage = async (event) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("image", event.target.files[0]);
      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      setInputError((pre) => ({ ...pre, image: false }));
      setInputValues((pre) => ({ ...pre, image: response?.data?.url }));
      openImageFile.current.value = null;
      handleOpenSnackBar("success", t("imageUploadSuccess"));
    } catch (error) {
      handleOpenSnackBar("error", t("imageUploadFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChageInputs = (e) => {
    const { name, value } = e.target;

    if (name === "companyPercentage" && (value > 100 || value < 0)) return;

    setInputError((pre) => ({
      ...pre,
      [name]: false,
    }));

    setInputValues((pre) => ({
      ...pre,
      [name]: value,
    }));
  };

  const calculateAmountAfterPercentage = () => {
    const totalPer = parseFloat(inputValues.percentage) || 0;
    const totalAmount = parseFloat(inputValues.price) || 0;

    if (totalAmount === 0) return 0;

    const amountAfterPercentage = totalAmount + (totalAmount * totalPer) / 100;
    return Math.round(amountAfterPercentage);
  };

  const handleAddEditCategory = async () => {
    if (!inputValues.companyPercentage)
      return setInputError((err) => ({ ...err, companyPercentage: true }));
    if (
      paramsData?.data?.serviceSubCategoryId?.type === "area" &&
      !prices?.area
    )
      return setInputError((err) => ({ ...err, area: true }));
    if (
      paramsData?.data?.serviceSubCategoryId?.type === "room" &&
      !prices?.room
    )
      return setInputError((err) => ({ ...err, room: true }));
    if (
      paramsData?.data?.serviceSubCategoryId?.type === "room+area" &&
      !prices?.area
    )
      return setInputError((err) => ({ ...err, area: true }));
    if (
      paramsData?.data?.serviceSubCategoryId?.type === "room+area" &&
      !prices?.room
    )
      return setInputError((err) => ({ ...err, room: true }));
    if (
      paramsData?.data?.serviceSubCategoryId?.type === "interior+exterior" &&
      !prices?.interior
    )
      return setInputError((err) => ({ ...err, interior: true }));
    if (
      paramsData?.data?.serviceSubCategoryId?.type === "interior+exterior" &&
      !prices?.exterior
    )
      return setInputError((err) => ({ ...err, exterior: true }));

    let payload = {
      price: prices,
      percentage: inputValues.companyPercentage,
    };

    try {
      setIsLoading(true);
      const response = await updateCompanyOwnSerivce(
        paramsData?.data?._id,
        payload
      );
      if (response.data.status === "success") {
        handleClose();
        handleOpenSnackBar("success", response.data.message);
        paramsData.getData();
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        {/* Dialog Header   */}
        <Stack
          p={"10px 16px"}
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Typography variant="h6">{t("editSubCategory")}</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Dialog Content */}
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Label labelText={t("image")} />
              <Stack
                justifyContent={"center"}
                alignItems={"center"}
                border={`1px solid ${inputError.image ? "#d32f2f" : "#d7d7d7"}`}
                sx={{ cursor: "pointer", height: { xs: 200, md: 130 } }}
              >
                {inputValues.image && (
                  <img
                    src={inputValues.image}
                    alt={"upload-image"}
                    width={"100%"}
                    height={"100%"}
                  />
                )}
              </Stack>
              {inputError.image && (
                <FormHelperText sx={{ color: "#d32f2f" }}>
                  {t("imageRequired")}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Label labelText={t("activeIcon")} />
              <Stack
                justifyContent={"center"}
                alignItems={"center"}
                border={`1px solid ${inputError.image ? "#d32f2f" : "#d7d7d7"}`}
                sx={{ cursor: "pointer", height: { xs: 200, md: 130 } }}
              >
                {inputValues.activeIcon && (
                  <img
                    src={inputValues.activeIcon}
                    alt={"active-icon"}
                    width={"100%"}
                    height={"100%"}
                  />
                )}
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Label
                required
                labelText={t("serviceName")}
                htmlFor={"service-name"}
              />
              <TextInput
                required
                styleProps={globalStyle.textField}
                id={"catrgory-name"}
                placeholder={`${t("enter")} ${t("name")}`}
                value={
                  inputValues.name ? inputValues.name[currentLanguage] : ""
                }
                readonly
              />
            </Grid>
            <Grid item xs={12}>
              <Label
                required
                labelText={`${t("adminFeePerAppointment")} (%)`}
                htmlFor={"service-name"}
              />
              <TextInput
                required
                styleProps={globalStyle.textField}
                id={"catrgory-name"}
                placeholder={t("appointmentPercentage")}
                value={inputValues.percentage}
                InputEndIcon={"%"}
                readonly
              />
            </Grid>
            <Grid item xs={12}>
              <Label
                required
                labelText={`${t("companyFeePerAppointment")} (%)`}
                htmlFor={"service-name"}
              />
              <TextInput
                required
                styleProps={globalStyle.textField}
                id={"catrgory-name"}
                placeholder={t("appointmentPercentage")}
                inputError={inputError.companyPercentage}
                helperText={
                  inputError.companyPercentage && t("companyPercentageRequired")
                }
                type={"number"}
                name={"companyPercentage"}
                value={inputValues.companyPercentage}
                onChange={handleChageInputs}
                InputEndIcon={"%"}
              />
            </Grid>
            {paramsData?.data?.serviceSubCategoryId?.type === "area" && (
              <Grid item xs={6}>
                <Label
                  required
                  labelText={t("enterPerAreaCharges")}
                  htmlFor={"service-name"}
                />
                <TextInput
                  required
                  styleProps={globalStyle.textField}
                  id={"catrgory-name"}
                  placeholder={t("enterCost")}
                  InputEndIcon={CURRENCY}
                  inputError={inputError.area}
                  helperText={inputError.area && t("perAreaPriceRequired")}
                  type={"number"}
                  name={"area"}
                  value={prices.area}
                  onChange={(e) => {
                    if (e.target.value < 0) return;
                    setPrices((v) => ({ ...v, area: e.target.value }));
                    setInputError((err) => ({ ...err, area: false }));
                  }}
                />
              </Grid>
            )}
            {paramsData?.data?.serviceSubCategoryId?.type === "room+area" && (
              <>
                <Grid item xs={6}>
                  <Label
                    required
                    labelText={t("enterPerAreaCharges")}
                    htmlFor={"service-name"}
                  />
                  <TextInput
                    required
                    styleProps={globalStyle.textField}
                    id={"catrgory-name"}
                    placeholder={t("enterCost")}
                    InputEndIcon={CURRENCY}
                    inputError={inputError.area}
                    helperText={inputError.area && t("perAreaPriceRequired")}
                    type={"number"}
                    name={"area"}
                    value={prices.area}
                    onChange={(e) => {
                      setPrices((v) => ({ ...v, area: e.target.value }));
                      setInputError((err) => ({ ...err, area: false }));
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Label
                    required
                    labelText={t("enterPerRoomCharges")}
                    htmlFor={"service-name"}
                  />
                  <TextInput
                    required
                    styleProps={globalStyle.textField}
                    id={"catrgory-name"}
                    placeholder={t("enterCost")}
                    InputEndIcon={CURRENCY}
                    inputError={inputError.room}
                    helperText={inputError.room && t("perRoomPriceRequired")}
                    type={"number"}
                    name={"room"}
                    value={prices.room}
                    onChange={(e) => {
                      setPrices((v) => ({ ...v, room: e.target.value }));
                      setInputError((err) => ({ ...err, room: false }));
                    }}
                  />
                </Grid>
              </>
            )}

            {paramsData?.data?.serviceSubCategoryId?.type === "fixed" && (
              <Grid item xs={6}>
                <Label
                  required
                  labelText={t("enterServiceCharges")}
                  htmlFor={"service-name"}
                />
                <TextInput
                  required
                  styleProps={globalStyle.textField}
                  id={"catrgory-name"}
                  placeholder={t("enterCost")}
                  InputEndIcon={CURRENCY}
                  inputError={inputError.fixed}
                  helperText={inputError.fixed && t("serviceChargesRequired")}
                  type={"number"}
                  name={"fixed"}
                  value={prices.fixed}
                  onChange={(e) => {
                    if (e.target.value < 0) return;
                    setPrices((v) => ({ ...v, fixed: e.target.value }));
                    setInputError((err) => ({ ...err, fixed: false }));
                  }}
                />
              </Grid>
            )}

            {paramsData?.data?.serviceSubCategoryId?.type ===
              "interior+exterior" && (
              <>
                <Grid item xs={6}>
                  <Label
                    required
                    labelText={t("enterInteriorServiceCharges")}
                    htmlFor={"service-name"}
                  />
                  <TextInput
                    required
                    styleProps={globalStyle.textField}
                    id={"catrgory-name"}
                    placeholder={t("enterCost")}
                    InputEndIcon={CURRENCY}
                    inputError={inputError.interior}
                    helperText={
                      inputError.interior && t("interiorServiceChargesRequired")
                    }
                    type={"number"}
                    name={"interior"}
                    value={prices.interior}
                    onChange={(e) => {
                      if (e.target.value < 0) return;
                      setPrices((v) => ({ ...v, interior: e.target.value }));
                      setInputError((err) => ({ ...err, interior: false }));
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Label
                    required
                    labelText={t("enterExteriorServiceCharges")}
                    htmlFor={"service-name"}
                  />
                  <TextInput
                    required
                    styleProps={globalStyle.textField}
                    id={"catrgory-name"}
                    placeholder={t("enterCost")}
                    InputEndIcon={CURRENCY}
                    inputError={inputError.exterior}
                    helperText={
                      inputError.exterior && t("exteriorServiceChargesRequired")
                    }
                    type={"number"}
                    name={"exterior"}
                    value={prices.exterior}
                    onChange={(e) => {
                      setPrices((v) => ({ ...v, exterior: e.target.value }));
                      setInputError((err) => ({ ...err, exterior: false }));
                    }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Label
                  required
                  labelText={t("categoryDescription")}
                  htmlFor={"category-description"}
                />
                <Typography variant="caption">
                  {inputValues.description
                    ? inputValues.description[currentLanguage].length
                    : 0}
                  /255
                </Typography>
              </Stack>
              <TextInput
                required
                styleProps={globalStyle.textField}
                placeholder={`${t("enter")} ${t("description")}`}
                value={
                  inputValues.description
                    ? inputValues.description[currentLanguage]
                    : ""
                }
                readonly
                multiline
                rows={5}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <GlobalButton
            variant={"outlined"}
            btnText={"cancel"}
            styleProps={{ width: 100, height: 40 }}
            handleClickBtn={handleClose}
          />
          <GlobalButton
            variant={"contained"}
            btnText={"save"}
            styleProps={{ width: 100, height: 40 }}
            handleClickBtn={handleAddEditCategory}
          />
        </DialogActions>
      </BootstrapDialog>
    </>
  );
});

export default EditServiceDialog;
